import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  Package, AlertTriangle, TrendingUp, TrendingDown,
  Warehouse, Plus, RefreshCw, Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL;

const PIE_COLORS = ["#232f3e", "#ff9900", "#37475a", "#687078", "#adb7bd", "#e3e7ea"];

// ─── small helpers ────────────────────────────────────────────────────────────
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

const StatCard = ({ icon: Icon, color, title, value, sub, trend }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3 shadow-sm min-w-0">
    <div className="flex items-center justify-between">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${color}`}>
        <Icon size={20} />
      </div>
      <span className="text-xs text-gray-400">{sub}</span>
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{fmtNum(value)}</div>
      <div className="text-sm text-gray-500 mt-0.5">{title}</div>
    </div>
    {trend !== undefined && (
      <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? "text-green-600" : "text-red-500"}`}>
        {trend >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
        {Math.abs(trend)}% vs last month
      </div>
    )}
  </div>
);

const Badge = ({ type }) => {
  const styles = {
    "Stock In":  "bg-green-100 text-green-700",
    "Stock Out": "bg-orange-100 text-orange-700",
    Adjustment:  "bg-blue-100 text-blue-700",
    Transfer:    "bg-purple-100 text-purple-700",
    Return:      "bg-yellow-100 text-yellow-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${styles[type] || "bg-gray-100 text-gray-600"}`}>
      {type}
    </span>
  );
};

// ─── Add Stock Modal ──────────────────────────────────────────────────────────
const AddStockModal = ({ product, onClose, onSuccess }) => {
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!qty || parseInt(qty) <= 0) return alert("Enter a valid quantity");
    setLoading(true);
    try {
      await axios.post(`${API}/inventory/add-stock/${product._id}`, {
        quantity: parseInt(qty),
        note,
        warehouse: "Bhubaneswar (Main)",
        performedBy: "Admin",
      });
      onSuccess();
      onClose();
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to add stock");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Add Stock</h2>
        <p className="text-sm text-gray-500 mb-4">
          Product: <span className="font-semibold text-gray-800">{product.name}</span>{" "}
          <span className="text-gray-400">(SKU: {product.sku})</span>
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Quantity *</label>
            <input
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
              placeholder="e.g. 50"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
              placeholder="Optional note"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="px-5 py-2 bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            {loading ? "Adding…" : "Add Stock"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const InventoryDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats]           = useState(null);
  const [movement, setMovement]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [lowStock, setLowStock]     = useState([]);
  const [outOfStock, setOutOfStock] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [movDays, setMovDays]       = useState(7);
  const [addStockFor, setAddStockFor] = useState(null); // product object

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, c, a, l, o] = await Promise.all([
        axios.get(`${API}/inventory/dashboard-stats`),
        axios.get(`${API}/inventory/stock-movement?days=${movDays}`),
        axios.get(`${API}/inventory/stock-by-category`),
        axios.get(`${API}/inventory/recent-activities?limit=5`),
        axios.get(`${API}/inventory/low-stock`),
        axios.get(`${API}/inventory/out-of-stock`),
      ]);
      setStats(s.data.stats);
      setMovement(m.data.movement || []);
      setCategories(c.data.categories || []);
      setActivities(a.data.activities || []);
      setLowStock(l.data.products || []);
      setOutOfStock(o.data.products || []);
    } catch (e) {
      console.error("Inventory fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [movDays]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pieData = categories.slice(0, 6).map((c) => ({
    name: c.name,
    value: c.totalStock,
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f3f3] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-[#b07d17] animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading inventory data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f3f3] font-sans">
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1">Dashboard › Inventory</nav>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Track, manage and optimize your product inventory across admin and seller stocks.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={fetchAll}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 bg-white rounded-lg text-sm hover:bg-gray-50 transition"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 bg-white rounded-lg text-sm hover:bg-gray-50 transition">
            <Download size={14} /> Export
          </button>
          <button
            onClick={() => navigate("/add-products")}
            className="flex items-center gap-2 px-4 py-1.5 bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg text-sm font-medium transition"
          >
            <Plus size={16} /> Add Stock
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard icon={Package}       color="bg-purple-500" title="Total Products"     value={stats?.totalProducts}   sub="All products"   trend={12} />
          <StatCard icon={Warehouse}     color="bg-green-500"  title="Total Stock (Units)"value={stats?.totalStock}      sub="Units"          trend={18} />
          <StatCard icon={AlertTriangle} color="bg-yellow-500" title="Low Stock Items"    value={stats?.lowStockItems}   sub="Below 20 units" trend={5} />
          <StatCard icon={TrendingDown}  color="bg-red-500"    title="Out of Stock Items" value={stats?.outOfStockItems} sub="Needs restock"  trend={-8} />
          <StatCard icon={Warehouse}     color="bg-blue-500"   title="Total Warehouses"   value={stats?.totalWarehouses || 1} sub="Active"   trend={0} />
        </div>

        {/* ── Charts Row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stock Movement Chart */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-bold text-gray-900">Stock Movement</h2>
              </div>
              <select
                value={movDays}
                onChange={(e) => setMovDays(Number(e.target.value))}
                className="text-sm border border-gray-200 rounded px-2 py-1 bg-gray-50 focus:outline-none"
              >
                <option value={7}>Last 7 Days</option>
                <option value={14}>Last 14 Days</option>
                <option value={30}>Last 30 Days</option>
              </select>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={movement} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="stockIn"  name="Stock In"      fill="#22c55e" radius={[3,3,0,0]} />
                <Bar dataKey="stockOut" name="Stock Out"     fill="#f97316" radius={[3,3,0,0]} />
                <Line type="monotone" dataKey="currentStock" name="Current Stock" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Stock by Warehouse (Pie) */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-900">Stock by Category</h2>
              <button
                onClick={() => navigate("/admin-inventory/stock-management")}
                className="text-xs text-[#007185] hover:underline"
              >
                View All
              </button>
            </div>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtNum(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {pieData.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-gray-700 truncate max-w-[120px]">{d.name}</span>
                      </div>
                      <span className="font-semibold text-gray-800">{fmtNum(d.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400 text-center mt-10">No category data</p>
            )}
          </div>
        </div>

        {/* ── Inventory Status Bar ── */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-4">Inventory Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "In Stock", value: (stats?.totalStock || 0) - (stats?.outOfStockItems || 0) * 1, color: "bg-green-100 text-green-700 border-green-200" },
              { label: "Low Stock", value: stats?.lowStockItems, color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
              { label: "Out of Stock", value: stats?.outOfStockItems, color: "bg-red-100 text-red-700 border-red-200" },
              { label: "Total Products", value: stats?.totalProducts, color: "bg-gray-100 text-gray-700 border-gray-200" },
            ].map((s) => (
              <div key={s.label} className={`flex flex-col items-center justify-center py-4 rounded-lg border ${s.color}`}>
                <div className="text-2xl font-bold">{fmtNum(s.value)}</div>
                <div className="text-sm font-medium mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Activities + Category Table ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Stock Activities */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Recent Stock Activities</h2>
              <button className="text-xs text-[#007185] hover:underline">View All</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date & Time</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Product</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Qty</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">By</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.length > 0 ? activities.map((a, i) => (
                    <tr key={a._id} className="border-b border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-2.5 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap">
                        {new Date(a.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-4 py-2.5"><Badge type={a.type} /></td>
                      <td className="px-4 py-2.5 text-gray-800 max-w-[140px] truncate">
                        {a.productName || a.product?.name?.en || "—"}
                      </td>
                      <td className="px-4 py-2.5 font-semibold text-gray-800">{a.quantity}</td>
                      <td className="px-4 py-2.5 text-gray-500">{a.performedBy}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">
                        No activity yet. Activities will appear here once purchases are received.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stock by Category Table */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Stock by Category</h2>
              <button
                onClick={() => navigate("/admin-inventory/stock-management")}
                className="text-xs text-[#007185] hover:underline"
              >
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Category</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Total Stock</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 text-yellow-600">Low</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 text-red-500">Out</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.slice(0, 6).map((cat, i) => (
                    <tr key={cat.categoryId || i} className="border-b border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-2.5 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{cat.name}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-800">{fmtNum(cat.totalStock)}</td>
                      <td className="px-4 py-2.5 text-right text-yellow-600 font-medium">{cat.lowStock}</td>
                      <td className="px-4 py-2.5 text-right text-red-500 font-medium">{cat.outOfStock}</td>
                    </tr>
                  ))}
                  {categories.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">No category data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Low Stock + Out of Stock ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Low Stock Products */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Low Stock Products</h2>
              <button
                onClick={() => navigate("/admin-inventory/low-stock")}
                className="text-xs text-[#007185] hover:underline"
              >
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Product</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">SKU</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Stock</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Reorder</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.length > 0 ? lowStock.map((p, i) => (
                    <tr key={p._id} className="border-b border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-2.5 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {p.image && <img src={p.image} alt="" className="w-8 h-8 object-contain rounded border border-gray-100" />}
                          <span className="text-gray-800 font-medium text-xs max-w-[110px] truncate">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{p.sku}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="font-bold text-orange-600">{p.currentStock}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-500">{p.reorderLevel}</td>
                      <td className="px-4 py-2.5 text-center">
                        <button
                          onClick={() => setAddStockFor(p)}
                          className="bg-[#b07d17] hover:bg-[#8c6312] text-white text-xs px-3 py-1 rounded transition"
                        >
                          Add Stock
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">
                        🎉 No low stock products!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Out of Stock Products */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Out of Stock Products</h2>
              <button
                onClick={() => navigate("/admin-inventory/low-stock")}
                className="text-xs text-[#007185] hover:underline"
              >
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Product</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">SKU</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Last Stocked</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {outOfStock.length > 0 ? outOfStock.map((p, i) => (
                    <tr key={p._id} className="border-b border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-2.5 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {p.image && <img src={p.image} alt="" className="w-8 h-8 object-contain rounded border border-gray-100" />}
                          <span className="text-gray-800 font-medium text-xs max-w-[110px] truncate">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{p.sku}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                        {p.lastStocked ? new Date(p.lastStocked).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <button
                          onClick={() => setAddStockFor(p)}
                          className="border border-red-400 text-red-600 hover:bg-red-50 text-xs px-3 py-1 rounded transition"
                        >
                          Restock
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                        🎉 All products are in stock!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Stock Modal */}
      {addStockFor && (
        <AddStockModal
          product={addStockFor}
          onClose={() => setAddStockFor(null)}
          onSuccess={() => {
            setAddStockFor(null);
            fetchAll();
          }}
        />
      )}
    </div>
  );
};

export default InventoryDashboard;
